/**
 * Générateur — Il résout des problèmes mettant en jeu des racines carrées.
 * Attendu ID : 203 | Niveau : 3ème | Domaine : Nombres (d11, O3)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, Créés n°1-2 de la banque 3e :
 * carré d'aire 17 cm², écran carré de diagonale 2 m). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « exacte »    — côté d'un carré d'aire N : valeur EXACTE (QCM, √ intapable)
 *   « approchee » — valeur approchée au millimètre (saisie, arrondi partagé)
 *   « fraction »  — le côté est-il une fraction d'entiers ? (QCM CALCULÉ :
 *                   la réponse dépend de « N carré parfait ou non »)
 *   « pythagore » — carré de diagonale d : 2c² = d² (trous exacts + approché)
 *   « vaetvient » — (√a)² = a et √(b²) = b (trous)
 *
 * Adaptation signalée : « donne la valeur exacte » ne peut pas être une saisie
 * (√ intapable au clavier) → QCM d'écritures. Arrondis PARTAGÉS gen↔solveur
 * (même calcul flottant + garde anti-pivot).
 */

import {
    randomChoice, randInt, tex, racineTex,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { estCarreParfait } from './nombres';

const ATTENDU_ID = 203;

const L_EXACTE = 'Donner la valeur exacte du côté d\'un carré d\'aire connue';
const L_APPROCHEE = 'Donner une valeur approchée d\'une racine carrée';
const L_FRACTION = 'Reconnaître si une racine carrée est un nombre entier';
const L_PYTHAGORE = 'Racine carrée et théorème de Pythagore';
const L_VAETVIENT = 'Le va-et-vient entre carré et racine carrée';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const CONTEXTES = [
    ['Un carré a une aire de', 'cm²', 'cm'],
    ['Un terrain carré a une aire de', 'm²', 'm'],
    ['Un carrelage carré a une aire de', 'cm²', 'cm'],
    ['Une nappe carrée a une aire de', 'dm²', 'dm'],
];

// N non carré parfait, à racine « propre » pour l'arrondi au dixième :
// loin d'un pivot d'arrondi et dernière décimale non nulle (convention 4e).
const tirerNonCarre = () => {
    for (let essai = 0; essai < 80; essai++) {
        const N = randInt(3, 99);   // N ≥ 3 : à N = 2, les distracteurs N² et 2N coïncident
        if (estCarreParfait(N)) continue;
        const r10 = Math.sqrt(N) * 10;
        const arrondi = Math.round(r10);
        if (Math.abs(r10 - Math.floor(r10) - 0.5) < 0.04) continue; // pivot d'arrondi
        if (arrondi % 10 === 0) continue;                            // dernière décimale nulle
        return N;
    }
    return 17; // repli (doc Créé n°1)
};

const arrondiDixiemeFr = (x) => {
    const v = Math.round(x * 10);
    return `${Math.floor(v / 10)},${v % 10}`;
};

// ═════════ « exacte » — valeur exacte du côté (QCM) ═════════

const genExacteQcm = () => {
    const N = tirerNonCarre();
    const [intro, u2, u1] = randomChoice(CONTEXTES);
    const correct = `${racineTex(N)} ${u1}`;
    const choix = [correct, `${tex(`\\dfrac{${N}}{2}`)} ${u1}`, `${N * N} ${u1}`, `${2 * N} ${u1}`];
    const payload = makeQcm(choix, correct,
        `<p>${intro} ${N} ${u2}.</p>`
        + '<p><b>Quelle est la valeur exacte de la longueur de son côté ?</b></p>',
        opts(L_EXACTE));
    payload._meta.params = { N, u1 };
    return payload;
};

// ═════════ « approchee » — valeur approchée au dixième (saisie) ═════════

const genApprocheeSaisie = () => {
    const N = tirerNonCarre();
    const [intro, u2, u1] = randomChoice(CONTEXTES);
    const rep = arrondiDixiemeFr(Math.sqrt(N));
    const payload = makeSaisie(rep,
        `<p>${intro} ${N} ${u2} ; son côté mesure donc ${racineTex(N)} ${u1}.</p>`
        + `<p><b>Donne la valeur approchée du côté arrondie au dixième de ${u1}</b>, `
        + 'c\'est-à-dire avec un seul chiffre après la virgule.</p>',
        { ...opts(L_APPROCHEE), suffixe: u1 });
    payload._meta.params = { N, rep };
    return payload;
};

// ═════════ « fraction » — entier / irrationnel ? (QCM calculé) ═════════

const genFractionQcm = () => {
    // Une fois sur deux, N est un carré parfait → la réponse BASCULE.
    let N;
    if (randInt(0, 1)) { const k = randInt(2, 12); N = k * k; }
    else N = tirerNonCarre();
    const parfait = estCarreParfait(N);
    const oui = 'Oui : c\'est même un nombre entier.';
    const non = 'Non : c\'est un nombre irrationnel.';
    const autre = 'Oui : il suffit de diviser l\'aire par 4.';
    const correct = parfait ? oui : non;
    const payload = makeQcm([oui, non, autre], correct,
        `<p>Le côté d'un carré d'aire ${N} cm² mesure ${racineTex(N)} cm.</p>`
        + '<p><b>Ce côté peut-il s\'écrire sous la forme d\'une fraction d\'entiers ?</b></p>',
        opts(L_FRACTION));
    payload._meta.params = { N, parfait };
    return payload;
};

// ═════════ « pythagore » — carré de diagonale d (trous) ═════════

const genPythagoreTrous = () => {
    const d = randomChoice([2, 4, 6, 8, 10]);        // pair → d²/2 entier
    const d2 = d * d;
    const h = d2 / 2;                                 // c² exact
    // Objet vraisemblable selon la taille de la diagonale.
    const objet = d <= 4
        ? randomChoice(['Un écran carré', 'Un plateau carré', 'Un cadre carré', 'Un vitrail carré'])
        : randomChoice(['Un terrain carré', 'Un jardin carré', 'Un enclos carré', 'Un bassin carré']);
    // Arrondi au centième PARTAGÉ (√h jamais pile sur un centième : h non carré
    // parfait et √h irrationnel pour h ∈ {2, 8, 18, 32, 50}).
    const approx = (Math.round(Math.sqrt(h) * 100) / 100).toFixed(2).replace('.', ',');
    const phrase = `$c^{2} + c^{2} = {{${d2}}} \\quad\\text{donc}\\quad c^{2} = {{${h}}}`
        + ` \\quad\\text{et}\\quad c = \\sqrt{${h}} \\approx {{${approx}}}\\ \\text{m}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${objet} a une diagonale de ${d} m. On appelle c son côté, en mètres.</p>`
        + '<p>D\'après le théorème de Pythagore dans un demi-carré : '
        + '<b>complète l\'égalité, puis la valeur approchée au centième</b>.</p>',
        { ...opts(L_PYTHAGORE), mode: 'saisie' });
    payload._meta.params = { d, d2, h, approx };
    return payload;
};

// ═════════ « vaetvient » — (√a)² = a ; √(b²) = b ═════════

const genVaetvientTrous = () => {
    let a; let b;
    do { a = randInt(2, 60); b = randInt(2, 30); } while (a === b || estCarreParfait(a));
    const phrase = `$\\left(\\sqrt{${a}}\\right)^{2} = {{${a}}} \\qquad \\sqrt{${b}^{2}} = {{${b}}}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Pour un nombre positif, élever au carré et prendre la racine carrée sont '
        + 'des opérations réciproques.</p><p><b>Complète sans calculatrice.</b></p>',
        { ...opts(L_VAETVIENT), mode: 'saisie' });
    payload._meta.params = { a, b };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    exacte_qcm_unique: genExacteQcm,
    approchee_texte: genApprocheeSaisie,
    fraction_qcm_unique: genFractionQcm,
    pythagore_texte_trou_saisie: genPythagoreTrous,
    vaetvient_texte_trou_saisie: genVaetvientTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'exacte', label: L_EXACTE, formatsList: buildFormats('exacte') },
    { id: 'approchee', label: L_APPROCHEE, formatsList: buildFormats('approchee') },
    { id: 'fraction', label: L_FRACTION, formatsList: buildFormats('fraction') },
    { id: 'pythagore', label: L_PYTHAGORE, formatsList: buildFormats('pythagore') },
    { id: 'vaetvient', label: L_VAETVIENT, formatsList: buildFormats('vaetvient') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
